// Executive dashboard — constants & small pure helpers.

import { ExecRange } from './types';

// ---------- Firestore collections ----------
export const DB_CLIENTS = 'clients';
export const DB_TELEPHONY_DAILY = 'telephony_daily';
export const DB_TELEPHONY_PORTFOLIO = 'telephony_portfolio_daily';
export const DB_TELEPHONY_SYNC = 'telephony_sync';
export const DB_TELEPHONY_CONFIG = 'telephony_config';
export const DB_TELEPHONY_ALERTS_CONFIG = 'telephony_alerts_config';
export const DB_INTAKE_OUTCOMES_PORTFOLIO = 'intake_outcomes_portfolio';
export const DB_STAFFING_MONTHLY = 'staffing_monthly';
export const DB_CLIENT_SHEETS = 'client_sheets';
// Business silos (Phase A) — synced daily from the CPA workbook + churn sheet.
export const DB_BUSINESS_MONTHLY = 'business_monthly';       // finance-gated
export const DB_PNL_MONTHLY = 'pnl_monthly';                 // finance-gated
export const DB_CLIENT_CHURN_MONTHLY = 'client_churn_monthly';
export const DB_TURNOVER_MONTHLY = 'turnover_monthly';
export const DB_SALES_MONTHLY = 'sales_monthly';
export const DB_SALES_PIPELINE = 'sales_pipeline';

// The staffing hours Google Sheet (source for staffing_monthly).
export const STAFFING_SHEET_URL = 'https://docs.google.com/spreadsheets/d/1zyv6qRuGVHEyrWFyyd2cCgZ-3QTHLer8Tc8JPmxwzh8/edit';
export const TELEPHONY_CONFIG_DOC = 'config';

// Insurance bucket labels (auto-bucketed from the free-text Insurance_Providers).
export const INSURANCE_BUCKET_LABELS: Record<string, string> = {
  commercial: 'Commercial',
  medicaid: 'Medicaid',
  medicare: 'Medicare',
  self_pay: 'Self-pay',
  unknown: 'Unknown',
};
export const INSURANCE_BUCKET_ORDER = ['commercial', 'medicaid', 'medicare', 'self_pay', 'unknown'];

// ---------- Intake-outcome evaluation exclusions ----------
// Clients disqualified from intake-OUTCOME metrics (conversion, confirmed-connected,
// admissions/first-time splits, insurance, etc.) because their outcome data is
// unreliable. Evolve's Salesforce admission events re-fire heavily (the "RR ID not
// found" retries), inflating conversions far past reality, and it has two center
// records pointing at one sheet — so it's excluded from evaluation. NOTE: this does
// NOT affect Five9 call-volume metrics (those are legitimate).
export const EXCLUDED_INTAKE_CLIENTS: RegExp[] = [/^evolve/i];
export function isExcludedIntakeClient(id: string): boolean {
  return EXCLUDED_INTAKE_CLIENTS.some((re) => re.test(id));
}

// ---------- Callable Cloud Function names ----------
export const FN_SYNC_TELEPHONY_NOW = 'syncTelephonyNow';

// ---------- Retention ----------
// Daily rollups are tiny; keep ~400 days so year-over-year trend is possible.
export const TELEPHONY_RETENTION_DAYS = 400;
export const TELEPHONY_RETENTION_MS = TELEPHONY_RETENTION_DAYS * 24 * 60 * 60 * 1000;

// ---------- Non-client campaigns (default exclusion set) ----------
// Overridable via telephony_config.excluded_campaigns; this is the seed/default.
export const EXCLUDED_CAMPAIGNS: string[] = [
  'Test-Campaign',
  'Dev-Campaign',
  'DEV-Test-Campaign',
  'Unassigned-Campaign',
  'Cold-Leads',
  'Sales-Prospects',
  'Transfer Calls by Client',
  'Answering-Service-Verification',
];

// ---------- Disposition map ----------
// Five9 reports return the FULL disposition NAME (e.g. "Admissions"), not a
// code. This maps those names to buckets; editable in telephony_config. Only
// admissions/hangup need mapping — non-admissions is derived (answered − admissions).
export const DEFAULT_DISPOSITION_MAP: Record<string, 'admissions' | 'nonadmissions' | 'hangup'> = {
  Admissions: 'admissions',
};

// ---------- Disposition groups (for the call-outcomes breakdown) ----------
// Groups the full Five9 disposition names into exec-meaningful buckets. Names
// not listed fall into 'other' (non-connect / junk / system dispositions).
export interface DispositionGroup {
  key: string;
  label: string;
  dispositions: string[];
}
export const DISPOSITION_GROUPS: DispositionGroup[] = [
  { key: 'admissions', label: 'Admissions', dispositions: ['Admissions'] },
  {
    key: 'connected',
    label: 'Connected, not admitted',
    dispositions: [
      'Non-admissions', 'Already Speaking to Facility', 'Answered - Yes Answering Service',
      'Answered - No Answering Service', 'Answered - Does Not Know', 'Facility Number',
      'Help Seeker Sober', 'Not Interested', 'Refused Treatment', 'Went Elsewhere',
    ],
  },
  {
    key: 'callback',
    label: 'Callback outcomes',
    dispositions: ['Call Back Expectation Provided', 'Call Back Tomorrow', 'Call Back 2 days', 'Call Back 7 days'],
  },
  { key: 'warm_transfer', label: 'Warm transferred', dispositions: ['Warm Transferred'] },
  {
    key: 'non_connect',
    label: 'Non-connect / other',
    dispositions: [
      'Hang Up', 'No-Answer', 'Voicemail', 'Blocked Caller', 'DNC', 'Wrong Number',
      'Wrong Number- Recording', 'Wrong Number- Spoke to Someone', 'Testing Only', 'Skip',
      'ACW Timeout', 'Redial Disconnected Caller', 'Remove From List', '-',
    ],
  },
];

const DISPOSITION_TO_GROUP: Record<string, string> = (() => {
  const m: Record<string, string> = {};
  for (const g of DISPOSITION_GROUPS) for (const d of g.dispositions) m[d] = g.key;
  return m;
})();

// Categorize a disposition name into a group key. Unknown / '*'-prefixed system
// dispositions → 'non_connect'.
export function dispositionGroup(name: string): string {
  return DISPOSITION_TO_GROUP[name] || 'non_connect';
}

// ---------- Date ranges ----------
export const RANGE_LABELS: Record<ExecRange, string> = {
  thismonth: 'This month',
  lastmonth: 'Last month',
  '7d': 'Last 7 days',
  '30d': 'Last 30 days',
  '90d': 'Last 90 days',
  ytd: 'Year to date',
  custom: 'Custom range…',
};

export const RANGE_ORDER: ExecRange[] = ['thismonth', 'lastmonth', '7d', '30d', '90d', 'ytd', 'custom'];

// ---------- Channel (from the intake "Serviced By" column) ----------
export const CHANNEL_ORDER: import('./types').ChannelFilter[] = ['all', 'human', 'ai', 'outbound'];
export const CHANNEL_LABELS: Record<import('./types').ChannelFilter, string> = {
  all: 'All channels',
  human: 'Human agents',
  ai: 'AI Voice',
  outbound: 'Outbound',
};

// Number of days a range spans back from today (ytd handled separately; custom
// is bounded by the user's picked dates, so this is only a hint).
export function rangeDays(range: ExecRange): number {
  switch (range) {
    case '7d':
      return 7;
    case '30d':
      return 30;
    case '90d':
      return 90;
    case 'ytd':
      return 366; // upper bound; the query start is clamped to Jan 1 client-side
    case 'custom':
    default:
      return 30;
  }
}

// ---------- Per-client table columns (metric key → label) ----------
export const METRIC_COLUMNS: { key: string; label: string; kind: 'int' | 'pct' | 'sec' }[] = [
  { key: 'offered', label: 'Calls', kind: 'int' },
  { key: 'answered', label: 'Answered', kind: 'int' },
  { key: 'answer_rate', label: 'Answer %', kind: 'pct' },
  { key: 'abandon_rate', label: 'Abandon %', kind: 'pct' },
  { key: 'asa', label: 'ASA', kind: 'sec' },
  { key: 'admissions_count', label: 'Admissions', kind: 'int' },
];

// ---------- List sizing ----------
export const PORTFOLIO_WORKING_SET_CAP = 400; // day-docs; ≈ full telephony retention window
// Hard ceiling for a wide CUSTOM range (intake-outcome history goes back years).
// Newest-first + limit means a range wider than this shows its most-recent slice.
export const PORTFOLIO_MAX_CAP = 1500;

// ---------- Display helpers ----------

export function fmtWhen(ms: number | null | undefined): string {
  try {
    return ms ? new Date(ms).toLocaleString() : '—';
  } catch {
    return '—';
  }
}

export function fmtPct(x: number | null | undefined): string {
  if (x == null || Number.isNaN(x)) return '—';
  return `${(x * 100).toFixed(1)}%`;
}

export function fmtSecs(s: number | null | undefined): string {
  if (s == null || Number.isNaN(s)) return '—';
  if (s < 60) return `${Math.round(s)}s`;
  const m = Math.floor(s / 60);
  const r = Math.round(s % 60);
  return `${m}m ${r}s`;
}

export function fmtInt(n: number | null | undefined): string {
  if (n == null || Number.isNaN(n)) return '—';
  return n.toLocaleString();
}

// De-hyphenate a campaign name into a display name ("Alta-Centers" → "Alta Centers").
export function displayNameFromCampaign(campaign: string): string {
  return campaign.replace(/-/g, ' ').trim();
}
